import React, {useEffect, Fragment, useState} from "react";
import {Body, Brand, Garage, Gearbox, Generation, Motorization, PartnerConfig, Place, Service, Slot} from "@App/types";
import { useRouter } from 'next/router'
import useStore from "@App/store";
import {DayPicker} from "@App/components/common/DayPicker/DayPicker";
import {TimeSlotPicker} from "@App/components/common/TimeSlotPicker";
import {useDates} from "@App/hooks/useDates";
import {useBooking} from "@App/hooks/useBooking";
import {Alert} from "@App/components/common/Alert";
import {parseISO, startOfDay} from "date-fns";
import {Text} from "@eo-locale/react";
import {Transition} from "@headlessui/react";
import {SelectDropdown} from "@App/components/common/FormElements/SelectDropdown";
import {useVehicletree} from "@App/hooks/useVehicletree";
import {useServices} from "@App/hooks/graphql/queries/useServices";
import {ListButton} from "@App/components/common/ListButton";
import {FilterSkeleton} from "@App/components/Skeletons/FilterSkeleton";
import {GenerationSelectDropdown} from "@App/components/common/FormElements/GenerationSelectDropdown";
import {MotorizationSelectDropdown} from "@App/components/common/FormElements/MotorizationSelectDropdown";
import process from "node:process";

export type GarageProfileBookingProps = {
    garage: Garage
    id?: string
    className?: string
    brands: Brand[]
    onSubmit?: (slot: Slot) => void
    partner: PartnerConfig
};

export const GarageProfileBooking = React.memo<GarageProfileBookingProps>(({garage, id, brands, className, partner, onSubmit}) => {
    const state = useStore()
    const router = useRouter()
    const {formatDate} = useDates()

    const service = state.order.service
    const place = state.order.place

    const brand = state.order.brand
    const organization = state.order.organization
    const generation = state.order.generation
    const motorization = state.order.motorization
    const gearbox = state.order.gearbox
    const body = state.order.body

    const {generations, motorizations, loading} = useVehicletree();
    const { services } = useServices(partner.id)

    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search)
        if (queryParams.size !== 0 && queryParams.get("service") && services) {
                const serviceFromUrl = services.filter((service: Service) => service.id === queryParams.get("service"))
                if (serviceFromUrl.length > 0) {
                    state.setService(serviceFromUrl[0]);
                    state.setServiceFilter(serviceFromUrl[0]);
                }
            }
        }, [services]);


    const resetGearboxandBody = () => {
       state.setBodyfilter({id: 0, name: ''})
        state.setGearboxfilter({id: 0, name: '', formatted_name: ''})
    }


    const handleBrand = (value: string) => {
        if (brands) {
            const brand = brands.filter((brand: Brand) => brand.id === value)
            state.setBrandFilter(brand[0])
            state.setBrand(brand[0])
            state.setGenerationFilter({id: 0, name: ''})
            state.setGeneration({id: 0, name: ''})
            state.setMotorizationfilter({id: 0, name: ''})
            state.setMotorization({id: 0, name: ''})
            resetGearboxandBody()
        }
    }

    const handleGeneration = (value: string) => {
         if (generations) {
            const generation = generations.filter((generation: Generation) => generation.id.toString() === value)
            state.setGenerationFilter(generation[0])
            state.setGeneration(generation[0])
            state.setMotorizationfilter({id: 0, name: ''})
            state.setMotorization({id: 0, name: ''})
            resetGearboxandBody()
        }
    }

    const handleMotorization = (value: string) => {
        if (motorizations) {
            const motorization = motorizations.filter((motorization: Motorization) => motorization.id.toString() === value)
            state.setMotorizationfilter(motorization[0])
            state.setMotorization(motorization[0])
            resetGearboxandBody()
        }
    }

    const handleGearbox = (value: string) => {
        if (motorizations && state.filters.motorizationFilter) {
            const gearbox = state.filters?.motorizationFilter?.gearboxes?.filter((gearbox: Gearbox) => gearbox.id.toString() === value)
            if (gearbox){
                state.setGearbox(gearbox[0])
                state.setGearboxfilter(gearbox[0])

            }}
    }

    const handleBody = (value: string) => {
        if (motorizations) {
            const body = state.filters?.motorizationFilter?.bodies?.filter((body: Body) => body.id.toString() === value)
            if(body){
                state.setBody(body[0])
                state.setBodyfilter(body[0])
            }
        }
    }

    const {
        changeDay,
        daysAvailable,
        setDaysAvailable,
        setDaysLoading,
        nextSlot,
        selectedDay,
        isShowingServices,
        setIsShowingServices,
        selectedSlot,
        triedSlot,
        errorWhenBlockingSlot,
        loadingWhenBlockingSlot,
        selectFirstSlot,
        initiallySelectedDay,
        daysLoading,
        changeSlot,
        changeEarliestSlot,
        blockSlot,
        blockedSlot
    } = useBooking()

    const handleBooking = () => {
        if (!state.order.service) {
            setIsShowingServices(!isShowingServices)
        } else if (partner.shouldDisplayVehicleSelection && (!state.filters.generationFilter.id || !state.filters.motorizationFilter.id) && state.order.service && !selectedSlot) {
            toggleVehicleSelect()
        } else if (selectedSlot) {
            blockSlot(selectedSlot)
        }
    }

    useEffect(() => {
        if (selectedSlot && blockedSlot && blockedSlot.id) {
            // chooseSlot(blockedSlot)
            // onSubmit && onSubmit(selectedSlot)
            state.setSlot(blockedSlot)

            if (service && blockedSlot) {
                router.push({
                    pathname: '/' + partner.slug + '/buchung',
                    query: {
                        service: service.id,
                        garage: garage.id,
                        place: place && place.id != '-1' ? place.id : null,
                        slot: blockedSlot.id,
                        session: blockedSlot.session_id,
                        ...brand?.id && {brand: brand.id},
                        ...generation?.id && {generation: generation.id},
                        ...motorization?.id && {motorization: motorization.id},
                        ...gearbox?.id && {gearbox: gearbox.id},
                        ...body?.id && {body: body.id}
                    }
                })
            }
        }
    }, [blockedSlot])

    useEffect(() => {
        // reset selected day when changing service
        changeDay(null)
        setDaysLoading(true)
    }, [state.order.service])

    const toggleVehicleSelect = () => {
        let status = state.isVehicleSlectionOpen
        state.setIsVehicleSlectionOpen(!status)
    }

    return (
        <div id={id} className={'garageProfile__booking col-span-full mb-4'}>
            <div className={'h-min md:border rounded-lg md:shadow-lg relative md:sticky md:top-5 md:p-5 '  + (className ?? '')}>
                <h2 className={'text-lg font-serif font-semibold mb-4'}><Text id={'booking_headline'}/></h2>
                <div className={'w-full flex flex-col text-sm border border-gray-300 rounded-lg px-3 py-2 my-3 cursor-pointer ' + (isShowingServices ? 'rounded-b-none' : '')} onClick={() => setIsShowingServices(!isShowingServices)}>
                    <span className={'text-xs'}>Service</span>
                    <div className={'text-base text-gray-500'}>{state.order.service ? state.order.service.name : <Text id={'booking_select_service'}/>}</div>
                </div>
                <div className={'flex flex-col border border-t-0 border-gray-300 max-h-[200px] overflow-y-scroll rounded-b-lg ' + (isShowingServices ? '' : 'hidden')}>
                    {garage.availableServices && garage.availableServices.map(service => {
                        return <span className={'py-2 px-3 hover:text-white hover:bg-primary-200 cursor-pointer'} key={service.id} onClick={() => {state.setService(service); setIsShowingServices(!isShowingServices)}}>{service.name}</span>
                    })}
                </div>
                {partner.shouldDisplayVehicleSelection && (
                    <div className={'w-full flex flex-col text-sm border border-gray-300 rounded-lg px-3 py-2 my-3 cursor-pointer '}
                         onClick={toggleVehicleSelect}>
                        <span className={'text-xs'}>Fahrzeug</span>
                        <div className={'text-base text-gray-500'}>{state.order.brand?.id !== '0' ? state.order.brand?.name + ' ' + state.order.generation?.name : <Text id={'booking_select_vehicle'}/>}</div>
                    </div>
                )}
                {!state.order.service && (
                    <div className={'flex md:hidden flex-col border rounded-lg text-center text-gray-500 p-8 bg-gray-100 mt-5'}>
                        <i className={'fa-kit fa-calendar-arrow-up fa-fw text-gray-700 text-xl mb-3 block'}/>
                        <p><Text id={partner.shouldDisplayVehicleSelection ? 'booking_select_service_long_vehicle' : 'booking_select_service_long'}/></p>
                    </div>
                )}
                <Alert display={!daysAvailable && !daysLoading} title={<Text id={'booking_no_slots'}/>}/>
                {(nextSlot && garage && garage.available_days.length > 0 && (selectedDay == initiallySelectedDay)) && (
                    <Alert
                        display={startOfDay(parseISO(nextSlot.starts_at)) < startOfDay(parseISO(garage.available_days[0].available_at.toString()))}
                        title={
                            <Text id={'booking_error_nearest_slot_gone'} day={formatDate(garage.available_days[0].available_at, 'dd.')} newDay={formatDate(nextSlot.starts_at, 'dd.')}/>
                        }
                        duration={10000}/>
                )}
                <Alert
                    display={!!errorWhenBlockingSlot && !!triedSlot}
                    title={(
                        <>
                            <Text id={'booking_slot_taken_1'}/>
                            <b>&nbsp;{formatDate(triedSlot ? triedSlot.starts_at : new Date(), "EEEEEE., dd.MM.yyyy 'um' HH:mm 'Uhr'")}</b> <Text id={'booking_slot_taken_2'}/>
                        </>
                    )}/>
                {nextSlot && (
                    <Fragment>
                        <Text id={'booking_next_free_slot'}/> <span
                        className={`inline-block text-sm border rounded px-3 py-1 cursor-pointer ${nextSlot === selectedSlot ? 'text-white bg-primary border-primary' : 'text-primary border-neutral-700'}`}
                        onClick={selectFirstSlot}>
                            {formatDate(nextSlot['starts_at'], "EEEEEE., dd.MM.yyyy 'um' HH:mm 'Uhr'")}
                        </span>
                    </Fragment>
                )}



                {((state.order.service && !selectedDay && !partner.shouldDisplayVehicleSelection) || (state.order.service && !selectedDay && partner.shouldDisplayVehicleSelection && brand && motorization?.id != 0 && generation?.id != 0)) && (
                    <DayPicker onChange={changeDay} onNoAvailableDays={setDaysAvailable}
                               daysLoading={setDaysLoading} nextSlot={nextSlot} selectedDay={selectedDay}/>
                )}

                {selectedDay && (
                    <Fragment>
                        <div className="w-full flex flex-col text-sm border border-gray-300 rounded-lg px-3 py-2 my-3 cursor-pointer" onClick={() => changeDay(null)}>
                            <span className={'text-xs'}>Ausgewählter Tag</span>
                            <div className={'text-base text-gray-500 rounded-lg cursor-pointer '}>{formatDate(selectedDay, "EEEEEE. dd.MM")}</div>
                        </div>
                        <TimeSlotPicker day={selectedDay} selectedSlot={selectedSlot} daysLoading={daysLoading}
                                        onChange={changeSlot} onChangeEarliestSlot={changeEarliestSlot}/>
                    </Fragment>
                )}
                {selectedDay && selectedSlot && (
                    <div className={'flex justify-between items-center text-sm mt-5'}>Ausgewählter Termin: <span className={'text-base text-gray-500 border border-gray-300 rounded-lg py-2 px-3'}>{formatDate(selectedSlot.starts_at, 'EE dd.MM')} um {formatDate(selectedSlot.starts_at, 'HH:mm')} Uhr</span></div>
                )}
                <button
                    disabled={loadingWhenBlockingSlot || !daysAvailable}
                    className={'relative w-full bg-primary-500 px-8 py-3 mt-5 text-white rounded-lg text-center no-underline disabled:opacity-50 disabled:cursor-not-allowed'} onClick={handleBooking}>
                    {loadingWhenBlockingSlot && (
                        <Text id={'booking_blocking'}/>
                    )}
                    {!loadingWhenBlockingSlot && (
                        <Text id={'booking_submit'}/>
                    )}
                </button>
            </div>

            <Transition show={state.isVehicleSlectionOpen}
                        enter="transition-opacity duration-300"
                        enterFrom="opacity-0 h-0"
                        enterTo="opacity-100 h-screen"
                        leave="transition-opacity duration-300"
                        leaveFrom="opacity-100 h-screen"
                        leaveTo="opacity-0 h-0"
                        className={'w-screen h-screen bg-transparent left-0 bottom-0 text-black px-4 pt-20 md:pt-8 md:py-8 cursor-default z-[60] fixed'}
                        onClick={toggleVehicleSelect}
            >
                <div
                    className={'bg-white h-full md:h-auto w-full pt-10 max-w-screen-md md:mx-auto md:my-0 rounded-t-md md:rounded-md overflow-auto relative text-left'}
                    onClick={(e) => e.stopPropagation()}>
                    <div className={'vehicleFilterBlock px-8'}>
                        <h4 className={'text-lg leading-6 font-serif font-medium pb-6'}>
                            <i className={'fa-kit fa-car fa-fw text-primary text-base mr-2 mr-2 inline-block'}/>
                            Welches Fahrzeug?
                            <button className={'text-3xl float-right h-6 w-6 rounded-md text-black-500 right-4 top-3 absolute'}
                                    onClick={toggleVehicleSelect}>&times;
                            </button>
                        </h4>
                        <div className={'grid grid-cols-12 gap-x-2 pb-8'}>
                            <div className={'col-span-6 md:col-span-4 mb-2 relative'}>
                                <SelectDropdown id={'0'} placeholdertext={'Marke'} values={brands} onChange={handleBrand} value={state.filters.brandFilter.id}/>
                                <i className={'fa-kit fa-chevron-down fa-fw text-gray-700 text-md absolute right-4 top-[9px]'}/>
                            </div>
                            {(loading && state.order.brand?.id != '0') && (
                                <Fragment>
                                    <FilterSkeleton/>
                                    <FilterSkeleton/>
                                    <FilterSkeleton/>
                                </Fragment>
                            )}
                            {!loading && (
                                <Fragment>
                                    <div className={'col-span-6 md:col-span-4 mb-2 relative'}>
                                        <GenerationSelectDropdown id={'0'} placeholdertext={'Modell'} values={generations} onChange={handleGeneration} value={state.filters.generationFilter.id.toString()}/>
                                        <i className={'fa-kit fa-chevron-down fa-fw text-gray-700 text-md absolute right-4 top-[9px]'}/>
                                    </div>
                                    <div className={'col-span-6 md:col-span-4 mb-2 relative'}>
                                        <MotorizationSelectDropdown id={'0'} placeholdertext={'Motorisierung'} values={motorizations} onChange={handleMotorization} value={state.filters.motorizationFilter.id.toString()}/>
                                        <i className={'fa-kit fa-chevron-down fa-fw text-gray-700 text-md absolute right-4 top-[9px]'}/>
                                    </div>
                                    {state.filters.motorizationFilter.gearboxes && state.filters.motorizationFilter.gearboxes.length > 1 && (
                                        <div className={'col-span-6 md:col-span-4 mb-2 relative'}>
                                            <SelectDropdown id={'0'} placeholdertext={'Getriebe'} values={state.filters.motorizationFilter.gearboxes} onChange={handleGearbox} value={state.filters.gearboxFilter.id.toString()}/>
                                            <i className={'fa-kit fa-chevron-down fa-fw text-gray-700 text-md absolute right-4 top-[9px]'}/>
                                        </div>
                                    )}
                                    {state.filters.motorizationFilter.bodies && state.filters.motorizationFilter.bodies.length > 1 && (
                                        <div className={'col-span-6 md:col-span-4 mb-2 relative'}>
                                            <SelectDropdown id={'0'} placeholdertext={'Bauart'} values={state.filters.motorizationFilter.bodies} onChange={handleBody} value={state.filters.bodyFilter.id.toString()}/>
                                            <i className={'fa-kit fa-chevron-down fa-fw text-gray-700 text-md absolute right-4 top-[9px]'}/>
                                        </div>
                                    )}
                                </Fragment>
                                )}
                        </div>
                        <div className={'buttonContainer p-4 grid grid-cols-12 gap-y-2 border-t border-black-100 bg-white md:w-full sticky bottom-0 z-20'}>
                            <div className={'col-span-full md:col-span-6 md:order-1 md:ml-1 rounded-md'}>
                                <ListButton text={'apply'} color={'primary-600'} textcolor={'white'} onClick={toggleVehicleSelect} icon={'magnifying-glass'}/>
                            </div>
                            <div className={'col-span-full md:col-span-6 md:mr-1 border border-black-100 rounded-md'}>
                                <ListButton text={'cancel'} color={'white'} textcolor={'gray-700'} onClick={toggleVehicleSelect}/>
                            </div>
                        </div>
                    </div>
                </div>
            </Transition>


        </div>
    )
})

GarageProfileBooking.displayName = 'GarageProfileBooking';
